import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { defaultColDef } from 'components/AgGrid/AgGridTable/helpers';
import { getRowId } from 'components/AgGrid/utils';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalDialog } from 'components/ModalDialog';
import { UploadStatus } from 'components/UploadStatus';
import type { IErrorHandlingRequestState } from 'features/errorHandling/errorHandlingSlice';
import { RequestStatus } from 'features/request/requestSlice';
import type {
    ChapterEnum,
    ValidationMessage,
    WorkOrderChangeSeasonData,
    WorkOrderChangeUerVolumesTransferInput,
} from 'gql/graphql';
import { WorkOrderPriceTypeEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { Loader } from 'uikit';
import { replacerCost } from 'utils/replacerCost';

import * as R from 'ramda';
import type React from 'react';
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { SeasonsSelectForm } from './components/SeasonsSelectForm';
import { useMoveVolumesBetweenSeasonsColumns } from './hooks/useMoveVolumesBetweenSeasonsColumns';
import { useUerTransferUerVolumes } from './hooks/useUerTransferUerVolumes';
import { useUerWorkOrderChangeUerRemainders } from './hooks/useUerWorkOrderChangeUerRemainders';

import Style from './MoveVolumesBetweenSeasons.module.css';

interface IMoveVolumesBetweenSeasons {
    onClose: (isSuccess?: boolean) => void;
    workOrderChangeId: number;
    chapter: ChapterEnum | null | undefined;
    workOrderObjectId?: number;
    workOrderChangeObjectId?: number;
    workOrderChangePriceId?: number;
    workOrderPriceId?: number;
    refreshTableDataHandler: (objectIds: number[]) => void;
    workOrderChangeSeasons: WorkOrderChangeSeasonData[];
}

const title = 'Перенос объемов между сезонами для расценки';

export const MoveVolumesBetweenSeasons: React.FC<IMoveVolumesBetweenSeasons> = (props) => {
    const {
        onClose,
        workOrderChangeId,
        chapter,
        workOrderObjectId,
        workOrderChangeObjectId,
        workOrderChangePriceId,
        workOrderPriceId,
        refreshTableDataHandler,
        workOrderChangeSeasons,
    } = props;

    const { control, handleSubmit, setValue, setError, clearErrors, formState } = useForm({
        mode: 'onChange',
    });

    const seasonFrom = useWatch({ control, name: 'seasonFrom' });
    const seasonTo = useWatch({ control, name: 'seasonTo' });

    const [mtrCheckErrorState, setMtrCheckErrorState] = useState<IErrorHandlingRequestState | undefined>();

    const { status, items, coefficientsIds, volumesFieldsIds, mtrIds } = useUerWorkOrderChangeUerRemainders({
        seasonFromYear: seasonFrom?.label,
        chapter: chapter as ChapterEnum,
        workOrderChangeId,
        workOrderChangePriceId,
        workOrderPriceId,
    });

    const onTransferSuccess = useCallback(() => {
        refreshTableDataHandler(workOrderObjectId ? [workOrderObjectId] : []);
        onClose();
    }, [onClose, refreshTableDataHandler, workOrderObjectId]);

    const { uerTransferUerVolumesMakeRequest, uerWorkOrderChangeUerRemaindersStatus } = useUerTransferUerVolumes({
        onSuccess: onTransferSuccess,
    });

    const volumes = useWatch({ control, name: volumesFieldsIds });
    const hasVolumeDataToMove = useMemo(() => volumes?.some((v) => !!v), [volumes]);
    const volumesWithErrors = volumesFieldsIds?.filter((id) => !!formState.errors[id]);
    const btnConfirmDisabled = !R.isEmpty(formState.errors) || !hasVolumeDataToMove || seasonFrom?.id === seasonTo?.id;

    const columns = useMoveVolumesBetweenSeasonsColumns({
        setValue,
        setError,
        clearErrors,
        volumesWithErrors,
        coefficientsIds,
    });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onClose,
        });

    const isLoading = useMemo(() => status === RequestStatus.FETCHING, [status]);

    const onConfirm = useCallback(
        (formData) => {
            const uerItem = items.find((i) => i.type === WorkOrderPriceTypeEnum.Uer);
            const mtrItems = items.filter((i) => mtrIds.includes(i.id));

            const mtrVolumes = mtrItems.map((i) => ({
                id: i.id,
                volume: replacerCost(formData[`volume_${i.id}`]),
                remainder: replacerCost(i.remainder),
                code: i.code,
            }));

            let validation: ValidationMessage[] = [];
            let uerVolume = 0;

            if (uerItem) {
                uerVolume = replacerCost(formData[`volume_${uerItem.id}`]);

                if (uerItem.remainder === uerVolume) {
                    validation = mtrVolumes.filter((i) => i.volume < i.remainder).map((i) => ({ message: i.code }));
                }
            }

            if (validation.length) {
                setMtrCheckErrorState({
                    message: 'Перенесите объем по МТР в выбранный сезон.',
                    validation,
                });
            } else {
                const input: WorkOrderChangeUerVolumesTransferInput = {
                    chapter: chapter as ChapterEnum,
                    workOrderObjectId,
                    workOrderChangeObjectId,
                    sourceSeasonId: seasonFrom?.id,
                    targetSeasonId: seasonTo?.id,
                    targetData: volumesFieldsIds?.map((id) => {
                        const item = items.find((i) => String(i.id) === id.replace('volume_', ''));

                        return {
                            transferValue: replacerCost(formData[id]),
                            workOrderChangePriceId: item?.workOrderChangePriceId,
                            workOrderPriceId: item?.workOrderPriceId,
                        };
                    }),
                };

                uerTransferUerVolumesMakeRequest(input);
            }
        },
        [
            chapter,
            items,
            mtrIds,
            seasonFrom?.id,
            seasonTo?.id,
            uerTransferUerVolumesMakeRequest,
            volumesFieldsIds,
            workOrderChangeObjectId,
            workOrderObjectId,
        ],
    );

    const onMtrErrorsClose = useCallback(() => {
        setMtrCheckErrorState(undefined);
    }, []);

    useEffect(() => {
        items.forEach((item) => {
            setValue(`volume_${item.id}`, 0);
            clearErrors(`volume_${item.id}`);
        });
    }, [setValue, items, clearErrors]);

    const gridOptions = {
        headerHeight: 40,
    };

    return (
        <>
            <ModalDialog
                title={title}
                hasOverlay
                withTitleCloseButton
                onClose={handleOpenConfirmationDialog}
                onConfirm={handleSubmit(onConfirm)}
                btnConfirmLabel="Перенести"
                btnCancelLabel="Отменить"
                btnConfirmDisabled={btnConfirmDisabled}
                bodyWidth={800}
                isCommit={uerWorkOrderChangeUerRemaindersStatus === RequestStatus.FETCHING}
                isOpen
                isOnClickOutsideDisable
            >
                <div className={Style.moveVolumesBetweenSeasonsForm}>
                    <SeasonsSelectForm
                        control={control}
                        seasonTo={seasonTo}
                        seasonFrom={seasonFrom}
                        setValue={setValue}
                        clearErrors={clearErrors}
                        setError={setError}
                        workOrderChangeSeasons={workOrderChangeSeasons}
                    />
                    <div className="values-block">
                        <div style={{ height: '100%' }} className="ag-theme-alpine WorkOrderChangePrice">
                            {isLoading ? (
                                <Loader />
                            ) : (
                                <AgGridTable
                                    columnDefs={columns}
                                    rowData={items}
                                    defaultColDef={defaultColDef}
                                    getRowId={getRowId}
                                    gridOptions={gridOptions}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </ModalDialog>
            <UploadStatus
                isOpened={!!mtrCheckErrorState}
                handleCloseModal={onMtrErrorsClose}
                data={mtrCheckErrorState}
                titleText={title}
                status="error"
                btnText="Понятно"
            />
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

MoveVolumesBetweenSeasons.displayName = 'MoveVolumesBetweenSeasons';
